/**
 * Banner — an inline message strip (info / warning / danger / success). Used for
 * deploy notes, suspended/deactivated banners, and error surfacing. Token-driven.
 */

import React from 'react';
import { useTheme } from '@obrava/ui';

export type BannerTone = 'info' | 'warning' | 'danger' | 'success';

export interface BannerProps {
  readonly tone: BannerTone;
  readonly children: React.ReactNode;
  readonly style?: React.CSSProperties;
}

export function Banner(props: BannerProps): React.ReactElement {
  const { tone, children, style } = props;
  const theme = useTheme();

  const tones: Record<BannerTone, { bg: string; text: string }> = {
    info: { bg: theme.color.infoBg, text: theme.color.infoText },
    warning: { bg: theme.color.warningBg, text: theme.color.warningText },
    danger: { bg: theme.color.dangerBg, text: theme.color.dangerText },
    success: { bg: theme.color.successBg, text: theme.color.successText },
  };
  const palette = tones[tone];

  const composed: React.CSSProperties = {
    backgroundColor: palette.bg,
    color: palette.text,
    borderRadius: theme.radii.radius.md,
    padding: `${theme.spacing.space[4]}px ${theme.spacing.space[5]}px`,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
    lineHeight: theme.typography.lineHeight.normal,
    ...style,
  };

  return (
    <div role="status" style={composed}>
      {children}
    </div>
  );
}
